import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

const PricingModal = ({ plan, onClose, onSaved }) => {
  const { toast, show } = useToast();
  const isEdit = !!plan?.id;

  const [form, setForm] = useState({
    tag:      plan?.tag      || "Starter",
    name:     plan?.name     || "",
    desc:     plan?.desc     || "",
    price:    plan?.price    || "",
    period:   plan?.period   || "/ project",
    timeline: plan?.timeline || "",
    icon:     plan?.icon     || "fa-solid fa-seedling",
    featured: plan?.featured || false,
    order:    plan?.order    || 0,
  });

  // Features as editable list: [{t: "...", on: true}]
  const [features, setFeatures] = useState(
    plan?.features || [{ t: "", on: true }]
  );
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const updateFeature = (i, field, value) => {
    setFeatures((prev) => prev.map((f, idx) => idx === i ? { ...f, [field]: value } : f));
  };

  const addFeature = () => setFeatures((p) => [...p, { t: "", on: true }]);
  const removeFeature = (i) => setFeatures((p) => p.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { show("Name and price are required", "error"); return; }
    setSaving(true);
    const payload = { ...form, order: Number(form.order), features: features.filter((f) => f.t.trim()) };
    try {
      if (isEdit) {
        await authFetch(`${ENDPOINTS.pricing}/${plan.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      } else {
        await authFetch(ENDPOINTS.pricing, { method: "POST", body: JSON.stringify(payload) });
      }
      show(isEdit ? "Plan updated!" : "Plan created!");
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="adm-modal-backdrop" onClick={onClose}>
        <div className="adm-modal" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
          <div className="adm-modal-header">
            <h3 className="adm-modal-title">{isEdit ? "Edit Plan" : "Add Pricing Plan"}</h3>
            <button className="adm-modal-close" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="adm-modal-body">
              <div className="adm-form">

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Plan Name *</label>
                    <input name="name" className="adm-form-input" value={form.name}
                      onChange={handleChange} placeholder="Launchpad" required />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Tag / Badge</label>
                    <input name="tag" className="adm-form-input" value={form.tag}
                      onChange={handleChange} placeholder="Starter / Popular" />
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Description</label>
                  <textarea name="desc" className="adm-form-textarea" value={form.desc}
                    onChange={handleChange} placeholder="Short description of who this plan is for" style={{ minHeight: 70 }} />
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Price (KSh) *</label>
                    <input name="price" className="adm-form-input" value={form.price}
                      onChange={handleChange} placeholder="80,000" required />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Period</label>
                    <input name="period" className="adm-form-input" value={form.period}
                      onChange={handleChange} placeholder="/ project" />
                  </div>
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Timeline</label>
                    <input name="timeline" className="adm-form-input" value={form.timeline}
                      onChange={handleChange} placeholder="2 – 4 weeks" />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Icon</label>
                    <input name="icon" className="adm-form-input" value={form.icon}
                      onChange={handleChange} placeholder="fa-solid fa-seedling" />
                  </div>
                </div>

                <div style={{ display: "flex", gap: "1.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", color: "rgba(255,255,255,0.6)", fontSize: "0.875rem" }}>
                    <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} style={{ accentColor: "#0a6b5a" }} />
                    Mark as featured plan
                  </label>
                </div>

                {/* Features list */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                    <label className="adm-form-label" style={{ margin: 0 }}>Features</label>
                    <button type="button" onClick={addFeature} style={{
                      background: "rgba(10,107,90,0.1)", border: "1px solid rgba(10,107,90,0.3)",
                      borderRadius: 6, padding: "0.25rem 0.6rem", color: "#0a9b7c",
                      fontSize: "0.75rem", cursor: "pointer",
                    }}>
                      <i className="fa-solid fa-plus"></i> Add Feature
                    </button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {features.map((f, i) => (
                      <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <input
                          type="checkbox" checked={f.on}
                          onChange={(e) => updateFeature(i, "on", e.target.checked)}
                          style={{ accentColor: "#0a6b5a", flexShrink: 0 }}
                        />
                        <input
                          className="adm-form-input"
                          value={f.t}
                          onChange={(e) => updateFeature(i, "t", e.target.value)}
                          placeholder="e.g. Up to 5 pages"
                          style={{ flex: 1 }}
                        />
                        <button type="button" onClick={() => removeFeature(i)}
                          style={{ background: "none", border: "none", color: "rgba(239,68,68,0.5)", cursor: "pointer", fontSize: "0.875rem", padding: "0 0.25rem" }}>
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="adm-save-btn" disabled={saving}>
                {saving ? "Saving..." : <><i className="fa-solid fa-check"></i> {isEdit ? "Update" : "Create"}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

const AdminPricing = () => {
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const { toast, show }       = useToast();

  const load = useCallback(async () => {
    try {
      const data = await authFetch(ENDPOINTS.pricing);
      setPlans(data);
    } catch (err) {
      show(err.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { load(); }, [load]);

  const deletePlan = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"?`)) return;
    try {
      await authFetch(`${ENDPOINTS.pricing}/${plan.id}`, { method: "DELETE" });
      load();
      show("Plan deleted.");
    } catch (err) {
      show(err.message || "Delete failed", "error");
    }
  };

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Pricing Plans</h1>
          <p className="adm-page-sub">{plans.length} plans</p>
        </div>
        <button className="adm-add-btn" onClick={() => setModal({ type: "add" })}>
          <i className="fa-solid fa-plus"></i> Add Plan
        </button>
      </div>

      {loading ? (
        <div className="adm-loading"><div className="adm-loading-spinner"></div></div>
      ) : plans.length === 0 ? (
        <div className="adm-card"><div className="adm-empty">No pricing plans yet.</div></div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {plans.map((p) => (
            <div key={p.id} className="adm-card" style={{ position: "relative" }}>
              {p.featured && (
                <div style={{
                  position: "absolute", top: "-1px", left: "50%", transform: "translateX(-50%)",
                  background: "linear-gradient(135deg,#0a6b5a,#c3b996)",
                  padding: "0.2rem 0.875rem", borderRadius: "0 0 8px 8px",
                  fontSize: "0.7rem", fontWeight: 700, color: "white", letterSpacing: "0.05em",
                }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", paddingTop: p.featured ? "0.5rem" : 0 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: "rgba(10,107,90,0.15)", border: "1px solid rgba(10,107,90,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center", color: "#0a9b7c",
                }}>
                  <i className={p.icon || "fa-solid fa-tag"}></i>
                </div>
                <div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, color: "var(--adm-text)" }}>{p.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)" }}>{p.tag}</div>
                </div>
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <span style={{ fontFamily: "'Syne',sans-serif", fontSize: "1.6rem", fontWeight: 800, color: "#c3b996" }}>
                  KSh {p.price}
                </span>
                <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.4)", marginLeft: "0.25rem" }}>{p.period}</span>
              </div>

              {p.timeline && (
                <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", marginBottom: "0.75rem" }}>
                  <i className="fa-solid fa-clock" style={{ marginRight: "0.35rem" }}></i>{p.timeline}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1rem" }}>
                {(p.features || []).map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: f.on ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.25)" }}>
                    <i className={`fa-solid ${f.on ? "fa-check" : "fa-xmark"}`} style={{ color: f.on ? "#0a9b7c" : "rgba(255,255,255,0.2)", fontSize: "0.7rem" }}></i>
                    {f.t}
                  </div>
                ))}
              </div>

              <div className="adm-action-btns">
                <button className="adm-edit-btn" onClick={() => setModal({ type: "edit", item: p })} style={{ flex: 1, justifyContent: "center" }}>
                  <i className="fa-solid fa-pen"></i> Edit
                </button>
                <button className="adm-delete-btn" onClick={() => deletePlan(p)}>
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal?.type === "add" && <PricingModal plan={null} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "edit" && <PricingModal plan={modal.item} onClose={() => setModal(null)} onSaved={load} />}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminPricing;
